/**
 * Chat Media Cache — IndexedDB
 *
 * Device-local store for image attachments sent in the AI chat, so restored
 * conversations show the actual image again instead of a nameless chip.
 *
 * PRIVACY: this deliberately complements — not changes — the server posture.
 * ChatSession persists attachment METADATA only (never bytes, by DPDP design);
 * the pixels live only on the device that sent them, same trust boundary as
 * the localStorage transcript mirror. Cross-device restores degrade to the
 * metadata chip exactly as before.
 *
 * DB:    cs_chat_media
 * Store: images  (key: attachment id, value: { key, dataUrl, name, savedAt })
 */

const DB_NAME = 'cs_chat_media';
const DB_VERSION = 1;
const STORE_NAME = 'images';
// Images are downscaled before send (~100-400KB each); 60 keeps the cache well
// under any sane IndexedDB quota while covering weeks of normal usage.
const MAX_IMAGES = 60;

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('savedAt', 'savedAt', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function withStore(mode, fn) {
  return openDB().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const result = fn(store);
    tx.oncomplete = () => { db.close(); resolve(result instanceof IDBRequest ? result._value : undefined); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    if (result instanceof IDBRequest) {
      result.onsuccess = () => { result._value = result.result; };
    }
  }));
}

/** Save a sent image (fire-and-forget; failures never block the send). */
export async function saveChatImage(id, dataUrl, name) {
  if (!id || !dataUrl) return;
  try {
    await withStore('readwrite', (store) => store.put({ key: String(id), dataUrl, name: name || '', savedAt: Date.now() }));
    await pruneOldest();
  } catch { /* quota/private-mode — restore just degrades to the chip */ }
}

/** Look up a cached image by attachment id. Resolves null when absent. */
export async function getChatImage(id) {
  if (!id) return null;
  try {
    const row = await withStore('readonly', (store) => store.get(String(id)));
    return row ? row.dataUrl : null;
  } catch {
    return null;
  }
}

/**
 * Wipe the whole device cache. Called on the privacy events where keeping
 * local pixels would betray the user's choice: going private, deleting all
 * chat history, and an account switch on this device (D-1a).
 */
export async function clearChatImages() {
  try {
    await withStore('readwrite', (store) => { store.clear(); return undefined; });
  } catch { /* private-mode / quota — nothing to clear */ }
}

/** LRU prune: keep the newest MAX_IMAGES rows. */
async function pruneOldest() {
  try {
    const keys = [];
    await new Promise((resolve, reject) => {
      openDB().then((db) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const idx = tx.objectStore(STORE_NAME).index('savedAt');
        const cursorReq = idx.openCursor();
        cursorReq.onsuccess = (e) => {
          const cursor = e.target.result;
          if (cursor) { keys.push(cursor.primaryKey); cursor.continue(); }
        };
        tx.oncomplete = () => { db.close(); resolve(); };
        tx.onerror = () => { db.close(); reject(tx.error); };
      }).catch(reject);
    });
    const excess = keys.length - MAX_IMAGES;
    if (excess > 0) {
      const stale = keys.slice(0, excess); // savedAt index → oldest first
      await withStore('readwrite', (store) => { stale.forEach((k) => store.delete(k)); return undefined; });
    }
  } catch { /* best-effort */ }
}
